import {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useTranslation} from 'react-i18next';
import {BaseEdge, EdgeLabelRenderer, EdgeProps, getBezierPath, useStore, XYPosition} from 'reactflow';

export interface EdgeActions {
    readOnly: boolean;
    onInsertClick: (edgeId: string, anchorEl: HTMLElement, flowPoint: XYPosition) => void;
    onDeleteEdge: (edgeId: string) => void;
    /** The edge whose picker is open right now — its cluster stays up and its ＋ stays lit. */
    activeInsertEdgeId: string | null;
}

export const EdgeActionsContext = createContext<EdgeActions | null>(null);

const HOVER_STROKE_WIDTH = 24;

const CONTROL_SIZE = 24;

const HOVER_PAD = {width: 96, height: 64};

const SHOW_DELAY = 200;

const HIDE_DELAY = 100;

const InsertableEdge = ({
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    selected,
    style,
    markerEnd,
}: EdgeProps) => {
    const {t} = useTranslation();
    const actions = useContext(EdgeActionsContext);

    const [hovered, setHovered] = useState(false);
    const showTimer = useRef<number>();
    const hideTimer = useRef<number>();

    const connecting = useStore(state => Boolean(state.connectionStartHandle));

    const showToolbar = useCallback(() => {
        window.clearTimeout(hideTimer.current);
        showTimer.current = window.setTimeout(() => setHovered(true), SHOW_DELAY);
    }, []);

    const hideToolbar = useCallback(() => {
        window.clearTimeout(showTimer.current);
        hideTimer.current = window.setTimeout(() => setHovered(false), HIDE_DELAY);
    }, []);

    useEffect(() => {
        if (!connecting) return;

        window.clearTimeout(showTimer.current);
        window.clearTimeout(hideTimer.current);
        setHovered(false);
    }, [connecting]);

    useEffect(() => () => {
        window.clearTimeout(showTimer.current);
        window.clearTimeout(hideTimer.current);
    }, []);

    const [path, labelX, labelY] = getBezierPath({
        sourceX,
        sourceY,
        sourcePosition,
        targetX,
        targetY,
        targetPosition,
    });

    const editable = !!actions && !actions.readOnly;

    const active = actions?.activeInsertEdgeId === id;

    const visible = hovered || !!selected || active;

    return (
        <>
            <BaseEdge id={id} path={path} style={style} markerEnd={markerEnd}/>

            {editable && (
                <>
                    <path
                        d={path}
                        fill={'none'}
                        strokeOpacity={0}
                        strokeWidth={HOVER_STROKE_WIDTH}
                        style={{pointerEvents: connecting ? 'none' : 'stroke'}}
                        onPointerEnter={showToolbar}
                        onPointerLeave={hideToolbar}
                    />

                    <EdgeLabelRenderer>
                        <Box
                            className={'nodrag nopan'}
                            onPointerEnter={showToolbar}
                            onPointerLeave={hideToolbar}
                            sx={{
                                position: 'absolute',
                                transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
                                width: HOVER_PAD.width,
                                height: HOVER_PAD.height,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                pointerEvents: connecting ? 'none' : 'all',
                            }}
                        >
                            <Paper
                                elevation={2}
                                onFocus={showToolbar}
                                onBlur={hideToolbar}
                                sx={{
                                    display: 'flex',
                                    gap: 0.25,
                                    p: 0.25,
                                    borderRadius: 5,
                                    border: theme => `1px solid ${theme.palette.divider}`,
                                    opacity: visible ? 1 : 0,
                                    visibility: visible ? 'visible' : 'hidden',
                                    pointerEvents: visible ? 'all' : 'none',
                                    transition: visible
                                        ? 'opacity 120ms ease-out, visibility 0s'
                                        : 'opacity 120ms ease-out, visibility 0s 120ms',
                                }}
                            >
                                <Tooltip title={t('Insert node')}>
                                    <IconButton
                                        size={'small'}
                                        aria-label={t('Insert node')}
                                        sx={{
                                            width: CONTROL_SIZE,
                                            height: CONTROL_SIZE,
                                            transition: 'background-color 140ms ease-out, color 140ms ease-out',
                                            ...(active ? {
                                                backgroundColor: 'primary.main',
                                                color: 'primary.contrastText',
                                                '&:hover': {backgroundColor: 'primary.main'},
                                            } : {}),
                                        }}
                                        onClick={event => {
                                            event.stopPropagation();
                                            actions.onInsertClick(id, event.currentTarget, {x: labelX, y: labelY});
                                        }}
                                    >
                                        <AddIcon sx={{fontSize: 16}}/>
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={t('Delete connection')}>
                                    <IconButton
                                        size={'small'}
                                        aria-label={t('Delete connection')}
                                        sx={{
                                            width: CONTROL_SIZE,
                                            height: CONTROL_SIZE,
                                            '&:hover': {color: 'error.main'},
                                        }}
                                        onClick={event => {
                                            event.stopPropagation();
                                            actions.onDeleteEdge(id);
                                        }}
                                    >
                                        <DeleteOutlineIcon sx={{fontSize: 16}}/>
                                    </IconButton>
                                </Tooltip>
                            </Paper>
                        </Box>
                    </EdgeLabelRenderer>
                </>
            )}
        </>
    );
};

export default InsertableEdge;
